<script lang="ts">
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import Empty from "@retailer/svelte/LimitedOrderingProductRequests/Empty.svelte";
  import Footer from "@retailer/svelte/LimitedOrderingProductRequests/Footer.svelte";
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import LoadingSpinner from "@shared/svelte/Components/LoadingSpinner.svelte";
  import PaginationLarge from "@shared/svelte/Components/Pagination/Large.svelte";
  import RequestNoteModal from "@retailer/svelte/LimitedOrderingProductRequests/RequestNoteModal.svelte";
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";

  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import ProductApprovalStore from "@shared/svelte/Stores/ProductApprovalStore";

  import { HttpPromiseWithEntireResponse } from "@shared/svelte/Modules/ApiService";
  import { convertToLocalWithTime } from "@shared/svelte/Components/DateFormatter/LocaleString.svelte";
  import { pluralize } from "@shared/svelte/Modules/pluralize";

  import { params, goto } from "@roxi/routify";
  import { onMount } from "svelte";

  import type { LimitedOrderingProductRequest } from "@shared/types/LimitedOrderingProductRequest";

  enum SortOption {
    DateRequestedByAsc = "created_at_asc",
    DateRequestedByDesc = "created_at_desc",
    RequestedByAsc = "requested_by_asc",
    RequestedByDesc = "requested_by_desc",
    RespondedByAsc = "responded_by_asc",
    RespondedByDesc = "responded_by_desc",
  }

  const SortOptions = [
    {
      label: "Date requested by ascending",
      value: SortOption.DateRequestedByAsc,
      params: { sort: "created_at", order: "asc" },
    },
    {
      label: "Date requested by descending",
      value: SortOption.DateRequestedByDesc,
      params: { sort: "created_at", order: "desc" },
    },
    {
      label: "Requested by A - Z",
      value: SortOption.RequestedByAsc,
      params: { sort: "requested_by_users", order: "asc" },
    },
    {
      label: "Requested by Z - A",
      value: SortOption.RequestedByDesc,
      params: { sort: "requested_by_users", order: "desc" },
    },
    {
      label: "Responded by A - Z",
      value: SortOption.RespondedByAsc,
      params: { sort: "addressed_by_users", order: "asc" },
    },
    {
      label: "Responded by Z - A",
      value: SortOption.RespondedByDesc,
      params: { sort: "addressed_by_users", order: "desc" },
    },
  ];

  let showLimitedOrderingRequestNoteModal: boolean = false;
  let requestWithNote: LimitedOrderingProductRequest = null;

  const openRequestNoteModal = request => {
    requestWithNote = request;
    showLimitedOrderingRequestNoteModal = true;
  };

  let data: LimitedOrderingProductRequest[] = [];
  let page: number = parseInt($params.page || 1);
  let perPage: number;
  let totalEntries: number;
  let loading: boolean = true;
  let selectedRetailer = $ProductApprovalStore.retailers.find(
    retailer => retailer.id == $params.retailer,
  )?.id;
  let selectedSortOption = SortOptions[0].value;
  let sortParams = { sort: "created_at", order: "asc" };

  const selectRetailer = e => {
    const retailerId: string = e.currentTarget.value;
    page = 1;
    getRequestHistory({ page, retailer: retailerId });
    $goto("/product_approvals", { retailer: retailerId });
  };

  const handleSort = e => {
    const sortOption = e.currentTarget.value;
    page = 1;
    selectedSortOption = sortOption;
    sortParams = SortOptions.find(option => option.value === sortOption).params;
    getRequestHistory({ ...$params, page }, sortParams);
  };

  const getRequestHistory = async (params, sortOptions = {}) => {
    loading = true;

    try {
      const res = await HttpPromiseWithEntireResponse({
        method: "GET",
        url: "/api/retailer/limited_ordering_product_requests/get_request_history_by_group_director",
        data: { retailer_id: params.retailer, page: params.page, ...sortOptions },
      });

      data = await res.json();
      perPage = parseInt(res.headers.get("X-Pagination-Limit"));
      totalEntries = parseInt(res.headers.get("X-Pagination-Entries"));
    } catch (e) {
      data = [];
    }
    loading = false;
  };

  $: tablePagination = {
    perPage,
    totalEntries,
    page,
    goToPage: n => {
      page = n;
      getRequestHistory({ ...$params, page }, sortParams);
      window.scrollTo(0, 0);
    },
  };

  onMount(() => {
    getRequestHistory($params);
  });
</script>

<style>
  @media (max-width: 375px) {
    tr > td {
      padding: 4px 32px;
    }
  }
</style>

<div
  class="fizz-flex-row-responsive {$LayoutStore.isMobile
    ? 'fizz-inset-square-16'
    : 'fizz-space-between fizz-inset-vertical-24 fizz-inset-horizontal-32'}"
>
  <div class="fizz-flex-row-responsive">
    <select
      class="fizz-select fizz-text-strong fizz-text-brand"
      style={$LayoutStore.isMobile ? "width: 100%" : ""}
      name="product_approvals_retailer_select"
      id="product_approvals_retailer_select"
      bind:value={selectedRetailer}
      on:change={selectRetailer}
    >
      <option value="">All retail locations</option>
      {#each $ProductApprovalStore.retailers as retailer (retailer.id)}
        <option value={retailer.id}>{retailer.name}</option>
      {/each}
    </select>
    {#if data.length}
      <div class="fizz-text-medium fizz-text-subdued">
        Showing {pluralize("product", totalEntries, true)}
      </div>
    {/if}
  </div>

  <select
    class="fizz-select fizz-text-brand"
    style={$LayoutStore.isMobile ? "width: 100%" : ""}
    name="request_sort_select"
    id="request_sort_select"
    bind:value={selectedSortOption}
    on:change={handleSort}
  >
    {#each SortOptions as option (option.value)}
      <option value={option.value}>
        {selectedSortOption === option.value ? "Sort by: " : ""}{option.label}
      </option>
    {/each}
  </select>
</div>

{#if loading}
  <LoadingSpinner />
{:else if data.length === 0}
  <Empty />
{:else}
  <table
    class="fizz-table fizz-text-medium fizz-expand-16-on-small fizz-table-align-top fizz-stack-32"
    data-testid="request-history-table"
    style="--heading-col-width: 120px"
  >
    <thead>
      <tr>
        <th>Product</th>
        <th>Location</th>
        <th>State</th>
        <th>Requested by</th>
        <th>Date requested</th>
        <th class="fizz-nowrap">Responded by</th>
        <th>Response</th>

        <th class="request-note" />
        <th class="fizz-align-right" />
      </tr>
    </thead>

    <tbody>
      {#each data as productRequest}
        <tr>
          <td>
            <div class="fizz-flex-row">
              <ProductImage
                product={productRequest.product.product_line}
                height="100"
                width="75"
                fizz={true}
                className="fizz-stack-4 fizz-inline-8"
              />
              <div>
                <p class="fizz-text-strong product-description">
                  <UIRouterLink
                    classes="fizz-text-large"
                    stateName={"layout.product_detail"}
                    params={{ productId: productRequest.product.product_line.id }}
                  >
                    {productRequest.product.product_line.name}
                  </UIRouterLink>
                </p>
                <p class="fizz-subdued">Size: {productRequest.product.description}</p>
              </div>
            </div>
          </td>

          <td class="vertical-align-middle">
            {productRequest.retailer.name}
          </td>
          <td class="vertical-align-middle">
            {productRequest.retailer.state}
          </td>
          <td class="vertical-align-middle">
            {productRequest.requested_by_user.full_name}
          </td>
          <td class="vertical-align-middle">
            {convertToLocalWithTime(productRequest.created_at)}
          </td>
          <td class="vertical-align-middle">
            {#if productRequest.addressed_by_user !== null}
              {productRequest.addressed_by_user.full_name}
            {/if}
          </td>
          <td class="vertical-align-middle capitalize">
            {productRequest.request_status}
          </td>
          <td class:request-note={$LayoutStore.isDesktop} class="vertical-align-middle">
            {#if productRequest.request_note !== null}
              <button
                class="fizz-icon-button-small"
                aria-label="request-note"
                on:click={() => openRequestNoteModal(productRequest)}
              >
                <Icon type="Document" width="24" height="24" fill="#848484" />
              </button>
            {/if}
          </td>
        </tr>
      {/each}
    </tbody></table
  >
  <PaginationLarge pagination={tablePagination} />
  <Footer />
{/if}

<RequestNoteModal
  bind:request={requestWithNote}
  show={showLimitedOrderingRequestNoteModal}
  onClose={() => (showLimitedOrderingRequestNoteModal = false)}
/>
